import { Head, Link, router, usePage } from "@inertiajs/react"
import { toQueryString } from "@/lib/queryParams"
import { useTableDensity } from "@/lib/useTableDensity"
import { useTranslation } from "react-i18next"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteIcon from "@mui/icons-material/Delete"
import DownloadIcon from "@mui/icons-material/Download"
import EditIcon from "@mui/icons-material/Edit"
import AddIcon from "@mui/icons-material/Add"
import AssessmentIcon from "@mui/icons-material/Assessment"
import HistoryIcon from "@mui/icons-material/History"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import RefreshIcon from "@mui/icons-material/Refresh"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import RecordHistoryDrawer from "@/pages/history/RecordHistoryDrawer"
import {
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material"
import dayjs from "dayjs"
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_PaginationState,
    type MRT_SortingState,
    useMaterialReactTable,
} from "material-react-table"
import { ElementType, useCallback, useEffect, useMemo, useRef, useState } from "react"

type SortDirection = "asc" | "desc"

interface IExportRef { id: number; name: string }

interface IOrderRow {
    id: number
    order_code: string
    client_name: string
    project_name: string
    reference_name: string
    reference_paper_color: number
    status: string
    order_date: string
    updated_at: string
    general_info_programmed_kilograms: number
    general_info_programmed_total: number
    reference_numbers: number[]
    export: IExportRef | null
}


type Props = {
    orders: IOrderRow[]
    search: string
    sort: string
    direction: SortDirection
    pagination: { current_page: number; total_pages: number; total_count: number; per_page: number }
}

function DeleteDialog({ order, onClose, tableParams }: {
    order: IOrderRow | null
    onClose: () => void
    tableParams: Record<string, string | number>
}) {
    const { t } = useTranslation()
    const [processing, setProcessing] = useState(false)
    const [snapshot, setSnapshot] = useState<IOrderRow | null>(null)

    const handleDelete = () => {
        if (!snapshot) return
        setProcessing(true)
        const query = new URLSearchParams(tableParams as Record<string, string>).toString()
        router.delete(`/orders/${snapshot.id}?${query}`, {
            preserveScroll: true,
            onFinish: () => { setProcessing(false); onClose() },
        })
    }

    return (
        <Dialog open={order !== null} onClose={onClose} maxWidth="xs" fullWidth
            slotProps={{ transition: { onEnter: () => setSnapshot(order) } }}>
            <DialogTitle>{t("orders.deleteTitle")}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t("orders.deleteText", { code: snapshot?.order_code })}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={onClose} disabled={processing} size="small">{t("common.cancel")}</Button>
                <Button color="error" variant="contained" size="small" onClick={handleDelete} disabled={processing}>
                    {processing ? t("common.deleting") : t("common.delete")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default function OrdersIndex() {
    const { orders, search: initialSearch, sort, direction, pagination } = usePage<Props>().props
    const { t } = useTranslation()
    const PAPER_COLOR: Record<number, string> = { 0: "—", 1: t("orders.paperColor.white"), 2: "Kraft" }

    const [deleteOrder, setDeleteOrder] = useState<IOrderRow | null>(null)
    const [historyTarget, setHistoryTarget] = useState<{ type: string; id: number } | null>(null)
    const [reloading, setReloading] = useState(false)
    const { density, onDensityChange } = useTableDensity("orders")
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const [sorting, setSorting] = useState<MRT_SortingState>(
        sort ? [{ id: sort, desc: direction === "desc" }] : []
    )
    const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
        pageIndex: pagination.current_page - 1,
        pageSize: pagination.per_page,
    })
    const [globalFilter, setGlobalFilter] = useState(initialSearch ?? "")

    useEffect(() => { setGlobalFilter(initialSearch ?? "") }, [initialSearch])
    useEffect(() => { setSorting(sort ? [{ id: sort, desc: direction === "desc" }] : []) }, [sort, direction])
    useEffect(() => {
        setPaginationState({ pageIndex: pagination.current_page - 1, pageSize: pagination.per_page })
    }, [pagination.current_page, pagination.per_page])

    useEffect(() => {
        const key = "scroll_orders"
        const saved = sessionStorage.getItem(key)
        if (saved) {
            sessionStorage.removeItem(key)
            requestAnimationFrame(() => { window.scrollTo({ top: parseInt(saved) }) })
        }
        return router.on("before", () => { sessionStorage.setItem(key, String(window.scrollY)) })
    }, [])

    const visit = useCallback((params: Record<string, string | number>) => {
        router.get("/orders", params, { preserveState: true, preserveScroll: true })
    }, [])

    const tableParams = useMemo(() => ({
        search: globalFilter,
        sort: sorting[0]?.id ?? "",
        direction: (sorting[0]?.desc ? "desc" : "asc") as SortDirection,
        page: paginationState.pageIndex + 1,
        per_page: paginationState.pageSize,
    }), [globalFilter, sorting, paginationState])

    const handleDuplicate = useCallback((order: IOrderRow) => {
        const query = toQueryString(tableParams)
        router.post(`/orders/${order.id}/duplicate?${query}`, {}, { preserveScroll: true })
    }, [tableParams])

    const columns = useMemo<MRT_ColumnDef<IOrderRow>[]>(() => [
        {
            accessorKey: "order_code",
            header: t("orders.code"),
            size: 160,
            Cell: ({ row, cell }) => {
                const exp = row.original.export
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <span style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{cell.getValue<string>()}</span>
                        {exp && (
                            <Tooltip title={`${t("orders.inExport")}: ${exp.name}`}>
                                <IconButton
                                    size="small"
                                    color="success"
                                    component={Link as ElementType}
                                    href={`/exports/${exp.id}/edit`}
                                    onClick={e => e.stopPropagation()}
                                    sx={{ p: 0.25 }}
                                >
                                    <LocalShippingIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                )
            },
        },
        { accessorKey: "client_name", header: t("orders.client"), size: 160 },
        { accessorKey: "project_name", header: t("orders.project"), size: 200 },
        {
            id: "references",
            header: t("orders.references"),
            size: 120,
            enableSorting: false,
            accessorFn: row => row.reference_numbers.filter(Boolean).join(", ") || "—",
        },
        { accessorKey: "reference_name", header: t("orders.product"), size: 160, enableSorting: false, Cell: ({ cell }) => cell.getValue<string>() || "—" },
        {
            accessorKey: "reference_paper_color",
            header: t("orders.color"),
            size: 80,
            enableSorting: false,
            Cell: ({ cell }) => PAPER_COLOR[cell.getValue<number>()] ?? "—",
        },
        {
            accessorKey: "status",
            header: t("orders.status"),
            size: 100,
            enableSorting: false,
            muiTableBodyCellProps: { align: "center" },
            Cell: ({ cell }) => (
                <Chip
                    label={t(`orders.statuses.${cell.getValue<string>()}`)}
                    size="small"
                    color={cell.getValue<string>() === "active" ? "success" : "default"}
                />
            ),
        },
        {
            accessorKey: "general_info_programmed_kilograms",
            header: t("orders.progKgs"),
            size: 100,
            enableSorting: false,
            Cell: ({ cell }) => Number(cell.getValue<number>()).toFixed(2),
        },
        {
            accessorKey: "general_info_programmed_total",
            header: t("orders.totalKgs"),
            size: 100,
            enableSorting: false,
            Cell: ({ cell }) => Number(cell.getValue<number>()).toFixed(2),
        },
        {
            accessorKey: "order_date",
            header: t("common.date"),
            size: 110,
            Cell: ({ cell }) => cell.getValue<string>() ? dayjs(cell.getValue<string>()).format("DD-MM-YYYY") : "—",
        },
        {
            accessorKey: "updated_at",
            header: t("common.updatedAt"),
            size: 150,
            Cell: ({ cell }) => dayjs(cell.getValue<string>()).format("DD-MM-YYYY HH:mm"),
        },
    ], [t])

    const table = useMaterialReactTable({
        columns,
        data: orders,
        enableRowActions: true,
        positionActionsColumn: "last",
        enableColumnFilters: false,
        enableFullScreenToggle: false,
        initialState: { showGlobalFilter: !!initialSearch },
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        rowCount: pagination.total_count,
        onGlobalFilterChange: (updater) => {
            const val = typeof updater === "function" ? updater(globalFilter) : (updater ?? "")
            setGlobalFilter(val)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
                visit({
                    search: val,
                    sort: sorting[0]?.id ?? "",
                    direction: sorting[0]?.desc ? "desc" : "asc",
                    page: 1,
                    per_page: paginationState.pageSize,
                })
            }, 400)
        },
        onPaginationChange: (updater) => {
            const next = typeof updater === "function" ? updater(paginationState) : updater
            setPaginationState(next)
            visit({
                search: globalFilter,
                sort: sorting[0]?.id ?? "",
                direction: sorting[0]?.desc ? "desc" : "asc",
                page: next.pageIndex + 1,
                per_page: next.pageSize,
            })
        },
        onSortingChange: (updater) => {
            const next = typeof updater === "function" ? updater(sorting) : updater
            setSorting(next)
            visit({
                search: globalFilter,
                sort: next[0]?.id ?? "",
                direction: next[0]?.desc ? "desc" : "asc",
                page: 1,
                per_page: paginationState.pageSize,
            })
        },
        muiPaginationProps: {
            rowsPerPageOptions: [25, 50, 100, 200],
            showFirstButton: true,
            showLastButton: true,
        },
        renderRowActions: ({ row }) => {
            const editUrl = `/orders/${row.original.id}/edit?${toQueryString(tableParams)}`
            return (
                <Box sx={{ display: "flex" }}>
                    <Tooltip title={t("common.edit")}>
                        <IconButton color="info" size="small" component={Link as ElementType} href={editUrl}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.duplicate")}>
                        <IconButton color="warning" size="small" onClick={() => handleDuplicate(row.original)}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.downloadExcel")}>
                        <IconButton color="success" size="small" onClick={() => { window.location.href = `/orders/${row.original.id}/download_excel` }}>
                            <DownloadIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("history.openRecordHistory")}>
                        <IconButton size="small" onClick={() => setHistoryTarget({ type: "Order", id: row.original.id })}>
                            <HistoryIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.delete")}>
                        <IconButton color="error" size="small" onClick={() => setDeleteOrder(row.original)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        },
        onDensityChange,
        state: {
            showProgressBars: reloading,
            pagination: paginationState,
            globalFilter,
            sorting,
            density,
        },
    })

    const reportsUrl = useMemo(() =>
        `/orders/reports?${toQueryString(tableParams)}`
    , [tableParams])

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            <Head title={t("orders.title")} />

            <Box sx={{ mb: 2 }}>
                <Button LinkComponent={Link} href="/" size="small" startIcon={<ArrowBackIcon />} color="inherit">
                    {t("common.home")}
                </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>{t("orders.title")}</Typography>
                    <Tooltip title={t("common.reload")}>
                        <IconButton size="small" onClick={() => { setReloading(true); router.reload({ onFinish: () => setReloading(false) }) }}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button variant="outlined" component={Link as ElementType} href="/extrusions" size="small"
                        endIcon={<OpenInNewIcon fontSize="small" />}
                        sx={{ color: "text.primary", borderColor: "text.primary" }}>
                        {t("modules.extrusion")}
                    </Button>
                    <Button variant="outlined" component={Link as ElementType} href="/exports" size="small"
                        endIcon={<OpenInNewIcon fontSize="small" />}
                        sx={{ color: "text.primary", borderColor: "text.primary" }}>
                        {t("modules.exports")}
                    </Button>
                    <Button variant="outlined" startIcon={<AssessmentIcon />} component={Link as ElementType} href={reportsUrl} size="small">
                        {t("common.reports")}
                    </Button>
                    <Button
                        LinkComponent={Link}
                        href="/history?module=orders"
                        startIcon={<HistoryIcon />}
                        variant="outlined"
                        size="small"
                    >
                        {t("history.openHistory")}
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} component={Link as ElementType} href="/orders/new" size="small">
                        {t("orders.new")}
                    </Button>
                </Box>
            </Box>

            <MaterialReactTable table={table} />

            <DeleteDialog order={deleteOrder} onClose={() => setDeleteOrder(null)} tableParams={tableParams} />
            <RecordHistoryDrawer
                open={historyTarget !== null}
                onClose={() => setHistoryTarget(null)}
                type={historyTarget?.type ?? null}
                id={historyTarget?.id ?? null}
            />
        </Container>
    )
}
